/** @graphql cc **/

import type { FC } from 'react';
import React from 'react';
import { useIntl, defineMessages } from 'react-intl-next';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';

import { Inline, Stack, Text, Flex } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';

import { LazyEmojiComponentLoader } from '@confluence/emoji-title';
import { isValidEmojiOrFavicon } from '@confluence/emoji-title/entry-points/isValidEmojiOrFavicon';
import { getEmojiValueFromContent } from '@confluence/emoji-title/entry-points/transformer';

import type {
	AudioPopupHeaderQuery as AudioPopupHeaderQueryType,
	AudioPopupHeaderQueryVariables,
} from './__types__/AudioPopupHeaderQuery';

const AudioPopupHeaderQuery = gql`
	query AudioPopupHeaderQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			id
			title
			emoji: properties(key: "emoji-title-published") {
				nodes {
					id
					value
				}
			}
			history {
				createdBy {
					displayName
				}
				ownedBy {
					displayName
				}
			}
		}
	}
`;

const styles = cssMap({
	emojiWrapperStyles: {
		borderRadius: token('border.radius.200'),
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		height: '40px',
		width: '40px',
	},
});

const i18n = defineMessages({
	contentBy: {
		id: 'audio.audio-popup.header-content-by',
		defaultMessage: 'By {name}',
		description:
			'header byline to state the author of the content, passing in the name of the author',
	},
	closePopupLabel: {
		id: 'audio.audio-popup.header-close-popup',
		defaultMessage: 'Close popup',
		description: 'label for the close button in the header of the audio popup',
	},
});

type AudioPopupHeaderProps = {
	contentId: string;
	contentType: string;
	onClose: () => void;
};

export const AudioPopupHeader: FC<AudioPopupHeaderProps> = ({
	contentId,
	contentType,
	onClose,
}) => {
	const { formatMessage } = useIntl();

	const { data } = useQuery<AudioPopupHeaderQueryType, AudioPopupHeaderQueryVariables>(
		AudioPopupHeaderQuery,
		{
			variables: {
				contentId,
			},
			skip: !contentId,
		},
	);

	const ownerName = data?.singleContent?.history?.ownedBy?.displayName;
	const authorName = data?.singleContent?.history?.createdBy?.displayName;
	const emoji = getEmojiValueFromContent(data?.singleContent ?? {});
	const contentTitle = data?.singleContent?.title;

	const shouldUsePageOwner = ownerName && contentType === 'page';
	const titleBylineName = shouldUsePageOwner ? ownerName : authorName;

	let icon;
	if (isValidEmojiOrFavicon(emoji, contentType)) {
		icon = (
			<LazyEmojiComponentLoader
				emoji={emoji || ''}
				contentType={contentType}
				context="audioPopup"
				height={24}
				wrapper={({ children }: { children?: React.ReactNode }) => (
					<Flex xcss={styles.emojiWrapperStyles} alignItems="center" justifyContent="center">
						{children}
					</Flex>
				)}
			/>
		);
	}

	return (
		<Inline space="space.150" spread="space-between">
			<Inline space="space.100" alignBlock="center">
				{icon}
				<Stack space="space.050">
					<Heading size="xsmall">
						<Text weight="medium" size="medium" maxLines={1}>
							{contentTitle}
						</Text>
					</Heading>
					<Text size="small">{formatMessage(i18n.contentBy, { name: titleBylineName })}</Text>
				</Stack>
			</Inline>
			<Inline space="space.100">
				<IconButton
					icon={CrossIcon}
					label={formatMessage(i18n.closePopupLabel)}
					appearance="subtle"
					shape="circle"
					spacing="compact"
					onClick={onClose}
					testId="audio-popup-close-button"
				/>
			</Inline>
		</Inline>
	);
};
